import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Grid, Collapse, Stack } from '@mui/material'
import { useTheme } from '@mui/material'
import Platform from 'components/common/Platform'
import PortfolioAccount from './PortfolioAccount'
import { DownOutlined, UpOutlined } from '@ant-design/icons'

const createStyles = (theme) => ({
  provider: {
    cursor: 'pointer',
    borderBottom: `1px solid ${theme.palette.secondary.dark}`,
    margin: '0 -1rem 1rem -1rem',
    padding: '0 1rem 0.5rem 1rem',
    width: 'calc(100% + 2rem)',
    maxWidth: 'calc(100% + 2rem) !important',
    flexBasis: 'auto !important',
  },
})

export default function AccountsByProvider({
  accounts,
  provider,
  portfolio = {},
  unmappedOnly = false,
  accountFilter = 'all',
  addAccountsToPortfolio,
  accountsById = {},
  removeAccountsFromPortfolio,
  level = 1,
  isCollapsed = false,
}) {
  const theme = useTheme()
  const levelSpacing = theme.spacing(5 * level)
  const styles = createStyles(theme)

  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    setCollapsed(isCollapsed)
  }, [isCollapsed])

  const accountsCount = useMemo(() => Object.keys(accounts).length, [accounts])

  const onMainAccountSelected = useCallback(
    ({ isAccountChecked, account, subAccounts }) => {
      const accountsIdsSet = new Set([account.id])

      function traverse(accountData) {
        if (!accountData || typeof accountData !== 'object') return

        // Extract account ID if exists
        if (accountData.account && accountData.account.id) {
          accountsIdsSet.add(accountData.account.id)
        }

        // Traverse nested subAccounts
        if (accountData.subAccounts && typeof accountData.subAccounts === 'object') {
          Object.values(accountData.subAccounts).forEach((subAccount) => traverse(subAccount))
        }
      }

      // Start traversal for each top-level account
      Object.values(subAccounts).forEach((account) => traverse(account))

      const accountsIds = Array.from(accountsIdsSet)

      if (isAccountChecked) {
        addAccountsToPortfolio({
          portfolioId: portfolio.id,
          accountsIds: accountsIds.filter((id) => !portfolio.accountsIds.includes(id)),
        })
      } else {
        removeAccountsFromPortfolio({
          portfolioId: portfolio.id,
          accountsIds: accountsIds.filter((id) => portfolio.accountsIds.includes(id)),
        })
      }
    },
    [addAccountsToPortfolio, portfolio.accountsIds, portfolio.id, removeAccountsFromPortfolio]
  )

  return !accountsCount ? null : (
    <Grid container>
      {level === 1 && (
        <Grid
          item
          xs={12}
          onClick={() => setCollapsed(!collapsed)}
          sx={collapsed ? styles.provider : undefined}
        >
          <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
            {collapsed ? <UpOutlined /> : <DownOutlined />}
            <Platform platform={provider} />
          </Stack>
        </Grid>
      )}
      <Grid item xs={12}>
        <Collapse in={level === 1 ? collapsed : true}>
          <Grid container gap={1}>
            {Object.values(accounts)?.reduce((acc, { account, subAccounts }) => {
              if (!accountsById[account.id]) return acc
              const { unmapped, hasUnmappedSubAccounts } = accountsById[account.id]
              if (unmappedOnly && !unmapped && !hasUnmappedSubAccounts) {
                return acc
              }
              if (accountFilter === 'mapped' && !portfolio.accountsIds?.includes(account.id)) {
                return acc
              }
              if (
                accountFilter === 'mapped_elsewhere' &&
                (unmapped || portfolio.accountsIds?.includes(account.id))
              ) {
                return acc
              }

              acc.push(
                <Grid item xs={12} key={account.id} container gap={1}>
                  <Grid item pl={levelSpacing} xs={12}>
                    <PortfolioAccount
                      name={account.name}
                      accountType={account.accountType}
                      subAccountsCount={Object.keys(subAccounts).length}
                      isAccountInPortfolio={
                        portfolio.accountsIds?.includes(account.id) ? true : false
                      }
                      onCheckboxChanged={(isAccountChecked) =>
                        onMainAccountSelected({
                          isAccountChecked,
                          account,
                          subAccounts,
                        })
                      }
                      disabled={unmappedOnly && !unmapped}
                    />
                  </Grid>
                  <AccountsByProvider
                    portfolio={portfolio}
                    provider={provider}
                    accounts={subAccounts}
                    accountsById={accountsById}
                    unmappedOnly={unmappedOnly}
                    accountFilter={accountFilter}
                    addAccountsToPortfolio={addAccountsToPortfolio}
                    removeAccountsFromPortfolio={removeAccountsFromPortfolio}
                    level={level + 1}
                  />
                </Grid>
              )

              return acc
            }, [])}
          </Grid>
        </Collapse>
      </Grid>
    </Grid>
  )
}
